import { Link } from 'react-router-dom';
import { comics, categories, faqs, siteInfo } from '@/mocks/comics';
import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';

const popularComics = comics.slice(0, 4);
const latestComics = [...comics].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()).slice(0, 8);

export default function Home() {
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [subscribeMsg, setSubscribeMsg] = useState('');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setSubscribeStatus('success');
      setSubscribeMsg('订阅成功！感谢您的关注。');
      form.reset();
      return;
    }
    setSubscribeStatus('loading');
    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') body.append(key, value as string);
      });
      const res = await fetch('https://readdy.ai/api/form/d9pe0t9n2trhb35gvdkg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setSubscribeStatus('success');
        setSubscribeMsg('订阅成功！感谢您的关注。');
        form.reset();
      } else {
        throw new Error(parsed?.meta?.message || '订阅失败');
      }
    } catch (err: unknown) {
      setSubscribeStatus('error');
      setSubscribeMsg(err instanceof Error ? err.message : '订阅失败，请稍后重试。');
    }
  };

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': '韩漫之家 - 免费韩漫在线阅读平台',
    'description': siteInfo.description,
    'url': 'https://www.hanmanzhijia.com',
    'isPartOf': {
      '@type': 'WebSite',
      'name': '韩漫之家',
      'url': 'https://www.hanmanzhijia.com',
    },
  };

  return (
    <>
      <SEOHead
        title="免费韩漫大全 - 韩漫在线阅读"
        description={siteInfo.description}
        keywords="韩漫之家,免费韩漫大全,漫画网址,韩漫在线阅读,韩国漫画,漫画推荐,在线漫画"
        canonicalUrl="/"
        ogImage={popularComics[0]?.cover}
        schema={homeSchema}
      />

      {/* Hero Section */}
      <section className="relative w-full h-[520px] md:h-[640px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://readdy.ai/api/search-image?query=Artistic%20abstract%20Korean%20manhwa%20style%20composition%20with%20warm%20golden%20and%20crimson%20tones%2C%20soft%20ink%20wash%20texture%2C%20elegant%20brush%20strokes%2C%20dreamy%20atmosphere%2C%20modern%20art%20aesthetic%2C%20subtle%20comic%20panel%20elements%2C%20no%20text%2C%20no%20characters&width=1600&height=900&seq=hero-bg-home&orientation=landscape)`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 h-full flex flex-col items-center justify-center text-center">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 animate-fade-in-up font-heading">
            韩漫之家
          </h1>
          <p className="text-lg md:text-xl text-white/85 max-w-2xl mb-2 animate-fade-in-up animate-fade-in-up-delay-1">
            免费韩漫大全 · 韩漫在线阅读
          </p>
          <p className="text-sm md:text-base text-white/65 max-w-xl mb-8 animate-fade-in-up animate-fade-in-up-delay-2">
            简单网址，一键访问海量韩漫资源。您的漫画阅读快捷通道。
          </p>

          <div className="flex flex-col sm:flex-row gap-3 animate-fade-in-up animate-fade-in-up-delay-3">
            <Link
              to="/category/all"
              className="px-8 py-3.5 bg-primary-500 text-white rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer no-underline"
            >
              开始阅读
            </Link>
            <Link
              to="/about"
              className="px-8 py-3.5 bg-white/15 text-white rounded-full text-sm font-semibold whitespace-nowrap hover:bg-white/25 transition-colors cursor-pointer no-underline backdrop-blur-sm"
            >
              了解更多
            </Link>
          </div>

          <div className="flex items-center gap-8 mt-10 animate-fade-in-up animate-fade-in-up-delay-4">
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-white">{siteInfo.comicCount}</div>
              <div className="text-xs md:text-sm text-white/60 mt-1">漫画资源</div>
            </div>
            <div className="w-px h-10 bg-white/20" />
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-white">{siteInfo.dailyVisitors}</div>
              <div className="text-xs md:text-sm text-white/60 mt-1">日访问量</div>
            </div>
            <div className="w-px h-10 bg-white/20" />
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-white">{siteInfo.totalViews}</div>
              <div className="text-xs md:text-sm text-white/60 mt-1">总阅读量</div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3 font-heading">漫画分类</h2>
            <p className="text-foreground-600 text-sm md:text-base">探索不同类型的韩漫，找到属于你的最爱</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4">
            {categories.map((cat, idx) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`group flex flex-col items-center gap-2.5 p-4 md:p-5 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer no-underline animate-fade-in-up animate-fade-in-up-delay-${Math.min(idx + 1, 6)}`}
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 group-hover:bg-accent-500 group-hover:text-white transition-colors">
                  <i className={`${cat.icon} text-xl`}></i>
                </div>
                <span className="text-sm font-medium text-foreground-800 whitespace-nowrap group-hover:text-foreground-950">{cat.name}</span>
                <span className="text-xs text-foreground-500">{cat.count}部</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Comics */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2 font-heading">热门推荐</h2>
              <p className="text-foreground-600 text-sm">本周最受读者欢迎的韩漫作品</p>
            </div>
            <Link
              to="/category/all"
              className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap no-underline transition-colors cursor-pointer"
            >
              查看全部 <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line text-sm"></i></div>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {popularComics.map((comic, idx) => (
              <Link
                key={comic.id}
                to={`/comic/${comic.id}`}
                className="group bg-white rounded-xl overflow-hidden hover-shimmer transition-colors cursor-pointer no-underline"
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                      comic.status === '连载中'
                        ? 'bg-accent-500 text-white'
                        : 'bg-foreground-800 text-white'
                    }`}>
                      {comic.status}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill text-yellow-400 text-xs"></i></div>
                      <span className="text-white text-sm font-medium">{comic.rating}</span>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1 line-clamp-1 whitespace-nowrap overflow-hidden text-ellipsis">{comic.title}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500">{comic.author}</span>
                    <span className="text-xs text-foreground-400">{comic.views.toLocaleString()} 阅读</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Updates */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2 font-heading">最新更新</h2>
              <p className="text-foreground-600 text-sm">每日更新，精彩不断</p>
            </div>
            <Link
              to="/category/all"
              className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap no-underline transition-colors cursor-pointer"
            >
              查看全部 <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line text-sm"></i></div>
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
            {latestComics.map((comic, idx) => (
              <Link
                key={comic.id}
                to={`/comic/${comic.id}`}
                className="group flex gap-3 p-3 rounded-xl bg-white hover:bg-background-100 transition-colors cursor-pointer no-underline"
              >
                <div className="w-16 md:w-20 h-22 md:h-28 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex flex-col justify-between flex-1 min-w-0">
                  <div>
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 mb-1 leading-snug">{comic.title}</h3>
                    <span className="text-xs text-foreground-500">{comic.category}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-foreground-400">
                    <span>{comic.chapters.length}话</span>
                    <span>更新于 {comic.updateDate}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3 font-heading">常见问题</h2>
            <p className="text-foreground-600 text-sm">关于韩漫之家，您想了解的一切</p>
          </div>

          <div className="flex flex-col gap-3">
            {faqs.map((faq, idx) => (
              <details key={idx} className="group bg-white rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer text-foreground-900 font-medium text-sm md:text-base list-none">
                  {faq.question}
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-3 group-open:rotate-180 transition-transform">
                    <i className="ri-arrow-down-s-line text-foreground-500"></i>
                  </div>
                </summary>
                <div className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-xl mx-auto px-4 md:px-6 text-center">
          <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
            <i className="ri-mail-send-line text-2xl"></i>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3 font-heading">订阅更新通知</h2>
          <p className="text-foreground-600 text-sm mb-6">
            第一时间获取最新韩漫更新信息，不再错过任何精彩内容
          </p>

          <form
            onSubmit={handleSubscribe}
            data-readdy-form
            className="flex flex-col sm:flex-row gap-3"
          >
            <input
              type="email"
              name="email"
              required
              placeholder="请输入您的邮箱地址"
              className="flex-1 px-5 py-3 rounded-full border border-background-300 text-sm bg-white text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
            />
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="visually-hidden"
            />
            <button
              type="submit"
              disabled={subscribeStatus === 'loading'}
              className="px-8 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60"
            >
              {subscribeStatus === 'loading' ? '订阅中...' : '立即订阅'}
            </button>
          </form>

          {subscribeStatus !== 'idle' && (
            <p className={`mt-4 text-sm ${subscribeStatus === 'success' ? 'text-accent-600' : 'text-red-500'}`}>
              {subscribeMsg}
            </p>
          )}
        </div>
      </section>

      {/* FAQ Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            'mainEntity': faqs.map((faq) => ({
              '@type': 'Question',
              'name': faq.question,
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': faq.answer,
              },
            })),
          }),
        }}
      />
    </>
  );
}